// Date/time helpers for events. Two conventions, matching the backend and iOS:
//
// - Timed events are edited as instants through browser-local
//   <input type="datetime-local"> values.
// - All-day events are calendar days in the EVENT's timezone: a day starts
//   at event-local midnight (or the first instant after a DST gap that skips
//   midnight) and an all-day range ends at the first instant of the day after
//   its last day. Never derive these from browser-local midnights — the
//   browser may be in another timezone.
//
// Calendar days are "YYYY-MM-DD" strings throughout.

const pad = (n) => String(n).padStart(2, "0");

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

const partFormatters = new Map();

function partFormatter(timeZone) {
  const key = timeZone || "";
  if (!partFormatters.has(key)) {
    partFormatters.set(
      key,
      new Intl.DateTimeFormat("en-US", {
        timeZone: timeZone || undefined,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }
  return partFormatters.get(key);
}

// Wall-clock parts of `instant` in `timeZone` (browser-local when omitted).
export function zonedParts(instant, timeZone) {
  const date = instant instanceof Date ? instant : new Date(instant);
  const parts = {};
  for (const { type, value } of partFormatter(timeZone).formatToParts(date)) {
    if (type !== "literal") parts[type] = Number(value);
  }
  return parts;
}

// The calendar day `instant` falls on in `timeZone`.
export function dayInZone(instant, timeZone) {
  const { year, month, day } = zonedParts(instant, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

// Pure calendar arithmetic on "YYYY-MM-DD" (no timezone involved).
export function addDays(day, days) {
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function daysBetween(fromDay, toDay) {
  const toUtc = (day) => {
    const [y, m, d] = day.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(toDay) - toUtc(fromDay)) / 86400000);
}

// The first instant of `day` in `timeZone`, as an ISO string: local
// midnight, or the moment a DST change that skips midnight ends. Same rule
// as the backend's _starts_local_day. Local dates only move forward with
// time, so this binary-searches the first millisecond whose local day is
// `day`; every UTC offset in use lies within ±15h of UTC.
export function startOfDayInZone(day, timeZone) {
  const [y, m, d] = day.split("-").map(Number);
  const utcMidnight = Date.UTC(y, m - 1, d);
  let lo = utcMidnight - 15 * 3600000; // still the previous local day
  let hi = utcMidnight + 15 * 3600000; // already `day` (or later)
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (dayInZone(mid, timeZone) >= day) hi = mid;
    else lo = mid;
  }
  return new Date(hi).toISOString();
}

// An all-day [startsAt, endsAt) range as inclusive event-local days. The
// stored end is the exclusive start of the day after the last day.
export function allDayDays(startsAt, endsAt, timeZone) {
  const firstDay = dayInZone(startsAt, timeZone);
  const lastDay = dayInZone(new Date(new Date(endsAt).getTime() - 1), timeZone);
  return { firstDay, lastDay: lastDay < firstDay ? firstDay : lastDay };
}

// Inclusive event-local days → the instants the backend stores.
export function allDayInstants(firstDay, lastDay, timeZone) {
  return {
    starts_at: startOfDayInZone(firstDay, timeZone),
    ends_at: startOfDayInZone(addDays(lastDay, 1), timeZone),
  };
}

// ISO instant → browser-local "YYYY-MM-DDTHH:mm" for <input type="datetime-local">.
export function toDatetimeLocalValue(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

// <input type="datetime-local"> value (browser-local) → ISO instant.
// `new Date(...)` parses a datetime-local string as local time per spec.
export function fromDatetimeLocalValue(localValue) {
  if (!localValue) return "";
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

// "YYYY-MM-DD" shown as a date label without any timezone shifting.
export function formatDay(day, options) {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(undefined, {
    ...options,
    timeZone: "UTC",
  });
}
